<template>
    <section v-if="active || !pret" class="prescripteur-selection mb-4" aria-label="Prescripteur">
        <p v-if="chargement" role="status">Chargement des prescripteurs…</p>
        <p v-if="erreur" role="alert" class="text-danger">{{ erreur }}</p>
        <a v-if="connexionRequise" href="/login" class="btn btn-outline-primary mr-2">Se reconnecter à la caisse</a>
        <button v-if="!pret && !chargement" type="button" class="btn btn-outline-primary" @click="charger">Réessayer</button>
        <fieldset v-if="active && pret" :disabled="locked || sauvegarde || chargement">
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
                <label for="paiement-prescripteur" class="font-wgh-600 mb-0">Médecin prescripteur</label>
                <button type="button" class="btn btn-link px-0" @click="nouveau = !nouveau">{{ nouveau ? 'Revenir au choix' : '+ Ajouter un prescripteur' }}</button>
            </div>
            <div v-if="!nouveau">
                <multiselect id="paiement-prescripteur" :value="selectionCourante" :options="liste"
                    track-by="id" :custom-label="libellePrescripteur" :internal-search="false"
                    :loading="rechercheEnCours" :allow-empty="true" :clear-on-select="true"
                    :show-labels="false" placeholder="Rechercher un prescripteur par nom ou téléphone"
                    aria-label="Rechercher et sélectionner un prescripteur"
                    @search-change="rechercher" @input="choisirPrescripteur">
                    <span slot="noResult">Aucun prescripteur trouvé</span>
                    <span slot="noOptions">Aucun prescripteur trouvé</span>
                </multiselect>
                <small v-if="!prescripteurId" class="text-muted">Laissez ce champ vide si aucun prescripteur n’est associé au paiement.</small>
                <label v-if="prescripteurId" for="paiement-indication" class="mt-3">Indication clinique *</label>
                <textarea v-if="prescripteurId" id="paiement-indication" v-model="indication" class="form-control" rows="2" maxlength="2000"></textarea>
            </div>
            <div v-else>
                <div class="row">
                    <label class="col-md-6">Nom *<input v-model.trim="draft.nom" class="form-control" maxlength="120"></label>
                    <label class="col-md-6">Prénoms *<input v-model.trim="draft.prenoms" class="form-control" maxlength="120"></label>
                </div>
                <label class="d-block">Téléphone<input v-model.trim="draft.telephone" class="form-control" maxlength="40"></label>
                <p class="mt-3 mb-2">Configurez une commission pour une catégorie entière (<strong>Tout</strong>) ou pour un type précis. La base n’est disponible que pour un pourcentage appliqué à un type précis.</p>
                <div v-for="(regle, index) in draft.regles" :key="index" class="prescripteur-regle mb-3">
                    <div class="row">
                        <label class="col-md-4">Catégorie
                            <select class="form-control" :value="regle.categorie" :aria-label="'Catégorie ' + (index + 1)" @change="choisirCategorie(regle, $event.target.value)">
                                <option value="" disabled>Choisir une catégorie</option>
                                <option v-for="cat in categories" :key="cat.categorie" :value="cat.categorie" :disabled="categorieIndisponible(cat, regle)">{{ cat.libelle }}</option>
                            </select>
                        </label>
                        <label class="col-md-4">Type
                            <select class="form-control" :value="regle.prestation_id === null ? '' : regle.prestation_id" :aria-label="'Type de prestation ' + (index + 1)" :disabled="!regle.categorie" @change="choisirPrestation(regle, $event.target.value)">
                                <option value="" :disabled="regleExiste(regle.categorie, null, regle)">Tout</option>
                                <option v-for="type in typesDe(regle.categorie)" :key="type.id" :value="type.id" :disabled="regleExiste(regle.categorie, type.id, regle)">{{ type.libelle }} · {{ type.montant }} CDF</option>
                            </select>
                        </label>
                        <label class="col-md-4">Mode de rémunération
                            <select class="form-control" :value="regle.mode_remuneration" :aria-label="'Mode de rémunération ' + (index + 1)" @change="choisirMode(regle, $event.target.value)">
                                <option value="pourcentage">Pourcentage</option>
                                <option value="montant_fixe">Montant fixe</option>
                            </select>
                        </label>
                    </div>
                    <div class="row align-items-end">
                        <label class="col-md-4">{{ regle.mode_remuneration === 'pourcentage' ? 'Pourcentage' : 'Montant fixe (CDF)' }}
                            <input v-if="regle.mode_remuneration === 'pourcentage'" v-model="regle.pourcentage" class="form-control" inputmode="decimal" placeholder="%" :aria-label="'Pourcentage ' + (index + 1)">
                            <input v-else v-model="regle.montant_fixe" class="form-control" inputmode="numeric" min="0" step="1" placeholder="CDF" :aria-label="'Montant fixe ' + (index + 1)">
                        </label>
                        <label v-if="regle.mode_remuneration === 'pourcentage' && regle.prestation_id !== null" class="col-md-4">Base de rémunération (CDF)
                            <input v-model="regle.base_remuneration" class="form-control" inputmode="numeric" min="0" step="1" :aria-label="'Base de rémunération ' + (index + 1)">
                        </label>
                        <div v-else class="col-md-4 text-muted pb-2">Base : —</div>
                        <div class="col-md-4 text-md-right pb-2"><button type="button" class="btn btn-link text-danger" :aria-label="'Retirer la règle ' + (index + 1)" @click="draft.regles.splice(index, 1)">Retirer</button></div>
                    </div>
                </div>
                <button type="button" class="btn btn-link pl-0" :disabled="!peutAjouterRegle()" @click="ajouterRegle">+ Ajouter une règle</button>
                <div class="text-right"><button type="button" class="btn btn-primary" :disabled="sauvegarde" @click="creer">{{ sauvegarde ? 'Enregistrement…' : 'Enregistrer et sélectionner' }}</button></div>
            </div>
        </fieldset>
    </section>
</template>
<script>
import api from '@/api/_prescripteurs'

const rate = value => String(value).trim().replace(',', '.')
const validRate = value => /^(0|[1-9][0-9]{0,2})(\.[0-9]{1,2})?$/.test(rate(value)) && Number(rate(value)) <= 100
const amount = value => String(value === null || value === undefined ? '' : value).trim()
const validAmount = value => /^(0|[1-9][0-9]{0,12})$/.test(amount(value)) && Number(amount(value)) <= 9999999999999
const rulePrestationId = value => value === null || value === undefined || value === '' ? null : Number(value)
export default {
    props: { locked: Boolean },
    data: () => ({ active: false, pret: false, chargement: false, sauvegarde: false, erreur: '', connexionRequise: false, liste: [], total: 0,
        recherche: '', rechercheEnCours: false, rechercheSequence: 0, rechercheTimer: null,
        prescripteurId: null, selectionCourante: null, indication: '', nouveau: false, categories: [],
        draft: { schema_version: 3, nom: '', prenoms: '', telephone: '', regles: [] } }),
    mounted() { this.charger() },
    beforeDestroy() { if (this.rechercheTimer) clearTimeout(this.rechercheTimer); this.rechercheSequence += 1 },
    methods: {
        libellePrescripteur(item) { return item ? [item.nom, item.prenoms].filter(Boolean).join(' ') + (item.telephone ? ' — ' + item.telephone : '') : '' },
        choisirPrescripteur(item) {
            this.selectionCourante = item || null
            this.prescripteurId = item ? item.id : null
            if (!item) this.indication = ''
        },
        async charger() {
            this.chargement = true; this.pret = false; this.erreur = ''; this.active = false; this.connexionRequise = false
            try {
                const token = await api.access(), config = await api.activation(token)
                this.active = config.active === true
                if (this.active) {
                    const [result, categories] = await Promise.all([api.liste({ search: this.recherche }, token), api.catalogue(token)])
                    this.liste = result.data; this.total = result.total; this.categories = categories
                } else { this.choisirPrescripteur(null); this.nouveau = false }
                this.pret = true
            } catch (error) {
                const status = error.response && error.response.status
                this.connexionRequise = status === 401
                this.erreur = status === 401 ? 'Votre session caisse a expiré. Reconnectez-vous avant de reprendre le paiement.'
                    : status === 403 ? 'Votre compte ne dispose pas de l’accès caisse requis. Contactez un administrateur.'
                    : 'Impossible de vérifier les commissions. Réessayez avant de valider le paiement.'
            }
            finally { this.chargement = false }
        },
        rechercher(texte) {
            this.recherche = texte || ''
            if (this.rechercheTimer) clearTimeout(this.rechercheTimer)
            const sequence = ++this.rechercheSequence
            this.rechercheTimer = setTimeout(async () => {
                this.rechercheEnCours = true
                try {
                    const result = await api.liste({ search: this.recherche }, await api.access())
                    if (sequence !== this.rechercheSequence) return
                    this.liste = result.data; this.total = result.total; this.erreur = ''
                } catch (_) {
                    if (sequence === this.rechercheSequence) this.erreur = 'Recherche indisponible. Réessayez.'
                } finally { if (sequence === this.rechercheSequence) this.rechercheEnCours = false }
            }, 250)
        },
        valider() {
            if (!this.pret || this.chargement || this.sauvegarde) return false
            if (this.active && this.nouveau) { this.erreur = 'Enregistrez le nouveau prescripteur ou revenez au choix.'; return false }
            if (this.active && this.prescripteurId && !this.indication.trim()) { this.erreur = 'Renseignez l’indication clinique.'; return false }
            this.erreur = ''; return true
        },
        selection() { return { prescripteur_id: this.active ? this.prescripteurId : null, indication_clinique: this.active && this.prescripteurId ? this.indication.trim() : '' } },
        nouvelleRegle() { return { categorie: '', prestation_id: null, mode_remuneration: 'pourcentage', pourcentage: '', base_remuneration: null, montant_fixe: null } },
        ajouterRegle() {
            if (!this.peutAjouterRegle() || this.sauvegarde) return
            this.draft.regles.push(this.nouvelleRegle())
        },
        peutAjouterRegle() {
            if (!this.draft || this.draft.regles.length >= 200) return false
            return this.categories.some(categorie => {
                const types = [null].concat((categorie.prestations || []).map(type => type.id))
                return types.some(typeId => !this.regleExiste(categorie.categorie, typeId))
            })
        },
        typesDe(categorie) { return (this.categories.find(item => item.categorie === categorie) || {}).prestations || [] },
        typeChoisi(regle) { return this.typesDe(regle.categorie).find(type => Number(type.id) === rulePrestationId(regle.prestation_id)) || null },
        regleExiste(categorie, prestationId, ignore) {
            if (!this.draft) return false
            const type = rulePrestationId(prestationId)
            return this.draft.regles.some(regle => regle !== ignore && regle.categorie === categorie && rulePrestationId(regle.prestation_id) === type)
        },
        categorieIndisponible(categorie, regle) {
            if (regle.categorie === categorie.categorie) return false
            const types = [null].concat((categorie.prestations || []).map(type => type.id))
            return !types.some(typeId => !this.regleExiste(categorie.categorie, typeId, regle))
        },
        choisirCategorie(regle, categorie) {
            const selection = this.categories.find(item => item.categorie === categorie)
            if (!selection || this.sauvegarde) return
            this.$set(regle, 'categorie', selection.categorie)
            const typeDisponible = (selection.prestations || []).find(type => !this.regleExiste(selection.categorie, type.id, regle))
            const prestationId = this.regleExiste(selection.categorie, null, regle) && typeDisponible ? typeDisponible.id : null
            this.$set(regle, 'prestation_id', prestationId)
            this.$set(regle, 'base_remuneration', null)
            if (regle.mode_remuneration !== 'montant_fixe') {
                this.$set(regle, 'mode_remuneration', 'pourcentage')
                this.$set(regle, 'pourcentage', regle.pourcentage === null ? '' : regle.pourcentage)
                const type = this.typeChoisi(regle)
                this.$set(regle, 'base_remuneration', type ? String(type.montant) : null)
            }
        },
        choisirPrestation(regle, value) {
            if (this.sauvegarde) return
            this.$set(regle, 'prestation_id', rulePrestationId(value))
            if (regle.mode_remuneration !== 'pourcentage') return
            const type = this.typeChoisi(regle)
            this.$set(regle, 'base_remuneration', type ? String(type.montant) : null)
        },
        choisirMode(regle, mode) {
            if (this.sauvegarde) return
            this.$set(regle, 'mode_remuneration', mode)
            if (mode === 'montant_fixe') {
                this.$set(regle, 'pourcentage', null)
                this.$set(regle, 'base_remuneration', null)
                this.$set(regle, 'montant_fixe', regle.montant_fixe || '')
                return
            }
            this.$set(regle, 'montant_fixe', null)
            this.$set(regle, 'pourcentage', regle.pourcentage === null ? '' : regle.pourcentage)
            const type = this.typeChoisi(regle)
            this.$set(regle, 'base_remuneration', type ? String(type.montant) : null)
        },
        erreurValidation() {
            const draft = this.draft
            if (!draft || !draft.nom.trim() || !draft.prenoms.trim()) return 'Renseignez le nom et les prénoms.'
            if (!Array.isArray(draft.regles) || draft.regles.length > 200) return 'Le nombre de règles est invalide.'
            const seen = new Set()
            for (const regle of draft.regles) {
                const categorie = this.categories.find(item => item.categorie === regle.categorie)
                if (!categorie) return 'Choisissez une catégorie disponible.'
                const prestationId = rulePrestationId(regle.prestation_id)
                const type = prestationId === null ? null : (categorie.prestations || []).find(item => Number(item.id) === prestationId)
                if (prestationId !== null && !type) return 'Choisissez un type tarifé dans cette catégorie.'
                const key = categorie.categorie + ':' + (prestationId === null ? 'tout' : prestationId)
                if (seen.has(key)) return 'Une seule règle est autorisée pour cette catégorie et ce type.'
                seen.add(key)
                if (regle.mode_remuneration === 'pourcentage') {
                    if (!validRate(regle.pourcentage)) return 'Le taux doit être compris entre 0 et 100 %, avec deux décimales maximum.'
                    if (amount(regle.montant_fixe)) return 'Le montant fixe ne s’applique pas à une règle au pourcentage.'
                    if (type) {
                        if (!validAmount(regle.base_remuneration)) return 'La base doit être un montant entier positif ou nul.'
                        if (Number(amount(regle.base_remuneration)) > Number(type.montant)) return 'La base ne peut pas dépasser le P.U. du type choisi.'
                    } else if (amount(regle.base_remuneration)) return 'La base est disponible uniquement pour un type précis.'
                } else if (regle.mode_remuneration === 'montant_fixe') {
                    if (!validAmount(regle.montant_fixe)) return 'Le montant fixe doit être un montant entier positif ou nul.'
                    if (amount(regle.pourcentage) || amount(regle.base_remuneration)) return 'Le pourcentage et la base ne s’appliquent pas à une règle au montant fixe.'
                } else return 'Choisissez un mode de rémunération.'
            }
            return ''
        },
        payloadCreation() {
            return {
                schema_version: 3,
                nom: this.draft.nom.trim(),
                prenoms: this.draft.prenoms.trim(),
                telephone: this.draft.telephone.trim(),
                regles: this.draft.regles.map(regle => {
                    const prestationId = rulePrestationId(regle.prestation_id)
                    return {
                        categorie: regle.categorie,
                        prestation_id: prestationId,
                        mode_remuneration: regle.mode_remuneration,
                        pourcentage: regle.mode_remuneration === 'pourcentage' ? rate(regle.pourcentage) : null,
                        base_remuneration: regle.mode_remuneration === 'pourcentage' && prestationId !== null ? Number(amount(regle.base_remuneration)) : null,
                        montant_fixe: regle.mode_remuneration === 'montant_fixe' ? Number(amount(regle.montant_fixe)) : null
                    }
                })
            }
        },
        async creer() {
            if (this.sauvegarde) return
            this.erreur = this.erreurValidation()
            if (this.erreur) return
            const draft = this.payloadCreation()
            this.sauvegarde = true
            try { const item = await api.enregistrer(draft, await api.access()); this.liste = [item, ...this.liste.filter(p => p.id !== item.id)]; this.choisirPrescripteur(item); this.nouveau = false; this.draft = { schema_version: 3, nom: '', prenoms: '', telephone: '', regles: [] } }
            catch (error) { const data = error.response && error.response.data; this.erreur = data && data.message ? data.message : 'Le prescripteur n’a pas été enregistré. Vérifiez les catégories, les types et la rémunération.' }
            finally { this.sauvegarde = false }
        }
    }
}
</script>
<style scoped>
.prescripteur-selection { background: #e8f1ff; padding: 20px; border-radius: 12px; }
.prescripteur-selection fieldset { min-width: 0; }
.prescripteur-selection .form-control { height: auto; min-height: 42px; }
.prescripteur-regle { border: 1px solid #c8d9f5; border-radius: 8px; padding: 12px; background: #fff; }
.prescripteur-regle label { font-weight: 600; }
</style>
